import { Sparkles, Hash, FileText, GitBranch, BarChart3 } from 'lucide-react'
import { useAuthContext } from '@/context'

type Suggestion = {
    title: string
    prompt: string
    icon: typeof Hash
    family: 'keywords' | 'documents' | 'pipeline' | 'analytics'
}

const SUGGESTIONS: Suggestion[] = [
    {
        title: 'Status overview',
        prompt: 'How many keywords do I have in each status?',
        icon: BarChart3,
        family: 'analytics',
    },
    {
        title: 'Recent documents',
        prompt: 'Show me my 5 most recently updated documents',
        icon: FileText,
        family: 'documents',
    },
    {
        title: 'Reserved keywords',
        prompt: 'List my reserved keywords with no working title',
        icon: Hash,
        family: 'keywords',
    },
    {
        title: 'Pipeline stages',
        prompt: 'Show me my pipeline groups and counts per stage',
        icon: GitBranch,
        family: 'pipeline',
    },
]

const MOBILE_SUGGESTIONS: Suggestion[] = [
    {
        title: 'Draft from my Foundation',
        prompt: 'Using my foundational profile, draft 3 versions of a short bio and 3 versions of a one-sentence tagline. If you need it, ask me to attach my Foundations.',
        icon: BarChart3,
        family: 'analytics',
    },
    {
        title: 'Review my Foundation',
        prompt: 'Review my foundational profile for gaps or unclear pieces. Ask me for any missing info, then suggest the smallest set of edits to make it stronger.',
        icon: FileText,
        family: 'documents',
    },
    {
        title: 'Plan my next campaign',
        prompt: 'Based on my goals, suggest 3 mobile-friendly campaign types to run next and what inputs I need to prepare.',
        icon: GitBranch,
        family: 'pipeline',
    },
    {
        title: 'Troubleshoot my account',
        prompt: 'I’m stuck. Ask me what I’m seeing and help me troubleshoot (billing/credits, campaigns not showing, or email verification).',
        icon: Hash,
        family: 'keywords',
    },
]

const FAMILY_STYLES: Record<string, string> = {
    keywords: 'bg-blue-100 text-blue-600',
    documents: 'bg-purple-100 text-purple-600',
    pipeline: 'bg-amber-100 text-amber-600',
    analytics: 'bg-emerald-100 text-emerald-600',
}

type Props = {
    onSelect: (prompt: string) => void
}

export default function EmptyState({ onSelect }: Props) {
    const { user } = useAuthContext()
    const isMobileConsumer = user?.experience_variant === 'mobile_consumer'
    const suggestions = isMobileConsumer ? MOBILE_SUGGESTIONS : SUGGESTIONS

    return (
        <div className='mx-auto w-full max-w-2xl px-4 py-6'>
            <div className='mb-6 text-center'>
                <div className='mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary-main/15 to-primary-main/5 ring-1 ring-primary-main/10'>
                    <Sparkles className='h-5 w-5 text-primary-main' />
                </div>
                <h1 className='text-lg font-semibold tracking-tight text-slate-900'>
                    How can I help?
                </h1>
                <p className='mx-auto mt-1 max-w-sm text-xs text-slate-500'>
                    {isMobileConsumer
                        ? 'Start with drafts and campaigns. If you already have a foundational profile, attach it and I’ll draft from it.'
                        : 'Ask about keywords, documents, or your pipeline. I can search, analyze, and make changes.'}
                </p>
            </div>

            <div className='grid gap-2 sm:grid-cols-2'>
                {suggestions.map(item => {
                    const Icon = item.icon
                    return (
                        <button
                            key={item.title}
                            type='button'
                            onClick={() => onSelect(item.prompt)}
                            className='group flex items-start gap-2.5 rounded-lg bg-white p-2.5 text-left ring-1 ring-slate-200 transition-all hover:-translate-y-0.5 hover:ring-primary-main/30 hover:shadow-md'
                        >
                            <div
                                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${FAMILY_STYLES[item.family]}`}
                            >
                                <Icon className='h-3.5 w-3.5' />
                            </div>
                            <div className='min-w-0'>
                                <div className='text-xs font-medium text-slate-900 group-hover:text-primary-main'>
                                    {item.title}
                                </div>
                                <div className='mt-0.5 text-[11px] text-slate-500 line-clamp-2'>
                                    {item.prompt}
                                </div>
                            </div>
                        </button>
                    )
                })}
            </div>
        </div>
    )
}
